import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TourIcon from '@mui/icons-material/Tour';

// ─── Tour scripts ──────────────────────────────────────────────────
//
// Steps are keyed by `${activeStep}:${connectionType}`. Each step targets
// a DOM element via `data-tour-id` so children don't need to pass refs.
// Empty `target` means the popover centers on the viewport.

const TOUR_STEPS = {
  'type:any': [
    {
      target: 'type-picker',
      title: 'Pick how you send us data',
      body: "Most clients pick Automated Email (your CRM emails reports automatically) or Manual Upload (drop a CSV yourself). Not sure? Pick Automated Email if your CRM supports scheduled reports — otherwise Manual Upload.",
    },
  ],

  'setup:email': [
    {
      target: 'setup-email-address',
      title: 'Send reports here',
      body: "Copy this address and give it to whoever manages your CRM. Reports sent here get auto-imported.",
    },
    {
      target: 'setup-subject',
      title: 'Keep the subject identical',
      body: "We match emails by subject line. Any typo or date change in your CRM will pause imports, so use something fixed like 'Daily Lead Report'.",
    },
    {
      target: 'setup-alltime',
      title: 'Send all-time data',
      body: "Leads can convert months after we get them. If your CRM only sends the last 7 days, we'll miss late revenue. All-time ensures we catch everything.",
    },
    {
      target: 'setup-confirm',
      title: 'Confirm and continue',
      body: "Check this box once you've sent a test email. We'll look for it automatically on the next step.",
    },
  ],

  'details:email': [
    {
      target: 'details-sender',
      title: 'Where your report comes from',
      body: "Paste the exact 'From' address your CRM uses. Check a real email you've received — typos here mean we won't find your reports.",
    },
    {
      target: 'details-subject',
      title: 'The subject line we match on',
      body: "Copy-paste this from a real email rather than typing it. We match character-for-character.",
    },
    {
      target: 'details-schedule',
      title: 'When we check',
      body: "Set this a few minutes after your CRM sends. E.g. CRM sends at 5:00 AM → set this to 5:15 AM.",
    },
  ],

  'details:api': [
    {
      target: 'api-endpoint',
      title: 'Your API URL',
      body: "The full HTTPS URL we'll call. Your API docs will have this — look for the 'leads' or 'contacts' endpoint.",
    },
    {
      target: 'api-auth',
      title: 'How to authenticate',
      body: "Most APIs use Bearer Token (an access token). Some use an API key in a custom header. Your API docs will tell you which.",
    },
    {
      target: 'api-method',
      title: 'GET or POST',
      body: "GET fetches data directly. POST is for APIs that need a filter body (like 'give me leads since this date'). Your docs will say which.",
    },
    {
      target: 'api-test',
      title: 'Test before saving',
      body: "Always click Test Connection first — it probes the endpoint with your credentials and shows exactly what the server replied. If this fails, the scheduled job will fail too.",
    },
  ],

  'details:google_sheet': [
    {
      target: 'sheet-service-account',
      title: 'Share your sheet first',
      body: "Copy this email and add it to your sheet's Share dialog (Viewer access is fine). This is the #1 thing people forget.",
    },
    {
      target: 'sheet-id',
      title: 'Paste the full URL',
      body: "The whole URL from your browser works fine — we extract the ID part automatically. Or paste just the ID if you prefer.",
    },
    {
      target: 'sheet-range',
      title: 'Which tab to read',
      body: "Use the tab name as it appears at the bottom of the spreadsheet. 'Leads!A:Z' reads columns A through Z of the Leads tab.",
    },
  ],

  'columns:any': [
    {
      target: 'columns-upload',
      title: 'Upload a sample file',
      body: "Drop any recent CSV or Excel file here. We'll auto-detect your columns and suggest which ones match our fields.",
    },
    {
      target: 'columns-coverage',
      title: 'Field coverage',
      body: "Green chips = mapped. Red chips = required but still missing. The more you map, the richer your reporting.",
    },
    {
      target: 'columns-table',
      title: 'Review or change suggestions',
      body: "Each row is one of your columns. The dropdown shows our suggested match — click it to pick a different one, or set to 'Skip' to ignore that column.",
    },
  ],

  'preview:any': [
    {
      target: 'preview-run',
      title: 'See the parsed data',
      body: "This processes your sample file with the mapping you just set up, without saving anything yet. Check that the numbers and values look right.",
    },
    {
      target: 'preview-counts',
      title: 'Row counts',
      body: "'Parsed OK' means we understood the row. 'Errors' usually means a required field (like date or revenue) was empty or malformed.",
    },
  ],
};

function resolveTarget(type, connectionType, activeStep, isManual, hasSetupStep) {
  if (activeStep === 0) return 'type:any';
  const STEP_SETUP = hasSetupStep ? 1 : -1;
  const STEP_DETAILS = isManual ? -1 : (hasSetupStep ? 2 : 1);
  const STEP_COLUMNS = isManual ? 1 : (hasSetupStep ? 3 : 2);
  const STEP_PREVIEW = isManual ? 2 : (hasSetupStep ? 4 : 3);
  if (activeStep === STEP_SETUP) return `setup:${connectionType}`;
  if (activeStep === STEP_DETAILS) return `details:${connectionType}`;
  if (activeStep === STEP_COLUMNS) return 'columns:any';
  if (activeStep === STEP_PREVIEW) return 'preview:any';
  return null;
}

// ─── Hook: manage tour open / step / auto-open ─────────────────────

export function useTour({ activeStep, connectionType, isManual, hasSetupStep }) {
  const [stepIndex, setStepIndex] = useState(-1); // -1 = closed

  const scriptKey = useMemo(
    () => resolveTarget(null, connectionType, activeStep, isManual, hasSetupStep),
    [activeStep, connectionType, isManual, hasSetupStep]
  );
  const steps = scriptKey ? TOUR_STEPS[scriptKey] || [] : [];

  const localStorageKey = scriptKey ? `dataIntegrationsTour:${scriptKey}:done` : null;

  const open = useCallback(() => {
    if (steps.length) setStepIndex(0);
  }, [steps.length]);

  const close = useCallback(() => {
    setStepIndex(-1);
    if (localStorageKey) {
      try { window.localStorage.setItem(localStorageKey, '1'); } catch (e) { /* ignore */ }
    }
  }, [localStorageKey]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i + 1 >= steps.length) {
        if (localStorageKey) {
          try { window.localStorage.setItem(localStorageKey, '1'); } catch (e) { /* ignore */ }
        }
        return -1;
      }
      return i + 1;
    });
  }, [steps.length, localStorageKey]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Auto-close when switching wizard steps
  useEffect(() => {
    setStepIndex(-1);
  }, [scriptKey]);

  // Auto-open on first visit to a given step/type pair
  useEffect(() => {
    if (!scriptKey || !steps.length) return;
    let done = '0';
    try { done = window.localStorage.getItem(localStorageKey) || '0'; } catch (e) { done = '0'; }
    if (done !== '1') {
      // small delay so target elements are mounted
      const t = setTimeout(() => setStepIndex(0), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [scriptKey, localStorageKey, steps.length]);

  return { stepIndex, steps, open, close, next, back };
}

// ─── Render: floating "Take a tour" button + active popover ────────

export function TourOverlay({ tour }) {
  const { stepIndex, steps, close, next, back } = tour;
  const currentStep = stepIndex >= 0 ? steps[stepIndex] : null;
  const [anchorEl, setAnchorEl] = useState(null);

  useLayoutEffect(() => {
    if (!currentStep) {
      setAnchorEl(null);
      return;
    }
    const target = currentStep.target
      ? document.querySelector(`[data-tour-id="${currentStep.target}"]`)
      : null;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setAnchorEl(target);
  }, [currentStep]);

  if (!currentStep) return null;

  // Overlay ring around target via absolutely positioned Box
  const rect = anchorEl?.getBoundingClientRect?.();

  return (
    <>
      {rect && (
        <Box
          sx={{
            position: 'fixed',
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 1299,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)',
            transition: 'top 120ms, left 120ms, width 120ms, height 120ms',
          }}
        />
      )}
      <Popper
        open={!!anchorEl || !currentStep.target}
        anchorEl={anchorEl}
        placement="bottom"
        sx={{ zIndex: 1300 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'preventOverflow', options: { padding: 16 } },
        ]}
      >
        <Paper elevation={8} sx={{ maxWidth: 360, p: 2 }}>
          <Stack direction="row" alignItems="start" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {stepIndex + 1} of {steps.length}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 0.25 }}>
                {currentStep.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {currentStep.body}
              </Typography>
            </Box>
            <IconButton size="small" onClick={close} aria-label="Close tour">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
            <Button size="small" onClick={back} disabled={stepIndex === 0}>
              Back
            </Button>
            <Button size="small" variant="contained" onClick={next}>
              {stepIndex + 1 >= steps.length ? 'Done' : 'Next'}
            </Button>
          </Stack>
        </Paper>
      </Popper>
    </>
  );
}

export function TourTriggerButton({ tour, sx }) {
  const { open, steps } = tour;
  if (!steps.length) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<TourIcon />}
      onClick={open}
      sx={sx}
    >
      Take a tour
    </Button>
  );
}
